import { traduzirErroSupabase } from '@octawash/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  CarFront,
  CheckCircle2,
  ChevronLeft,
  Clock,
  Droplets,
  MapPin,
  ScanLine,
  Sparkles,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth-context';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/lib/tenant-context';

interface Veiculo {
  seq_veiculo: number;
  num_placa: string;
  cod_apelido: string | null;
}

interface Maquina {
  seq_maquina: number;
  nom_maquina: string;
  tab_unidades: { nom_unidade: string };
}

interface TipoLavagem {
  seq_tipo_lavagem: number;
  cod_tipo_lavagem: string;
  nom_tipo_lavagem: string;
}

async function extrairMensagemErro(error: unknown): Promise<string> {
  if (!error) return 'Erro desconhecido';
  const e = error as {
    message?: string;
    context?: { body?: ReadableStream | Response };
  };
  try {
    const ctx = e.context;
    if (ctx?.body) {
      const txt =
        ctx.body instanceof Response
          ? await ctx.body.text()
          : await new Response(ctx.body as ReadableStream).text();
      try {
        const json = JSON.parse(txt) as { erro?: string; des_motivo?: string };
        return json.erro ?? json.des_motivo ?? txt;
      } catch {
        return txt;
      }
    }
  } catch {
    /* ignore */
  }
  return e.message ?? String(error);
}

/**
 * Pill selecionável usado em cada step (veículo, tipo, unidade).
 * Quando ativo: gradient + ring + checkmark animado.
 */
function PillSelect<T extends { value: string; label: string; subtitle?: string }>({
  opcoes,
  selecionado,
  aoSelecionar,
  icone: Icone,
}: {
  opcoes: T[];
  selecionado: string;
  aoSelecionar: (v: string) => void;
  icone: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="octa-stagger grid gap-2 sm:grid-cols-2">
      {opcoes.map((o) => {
        const ativo = selecionado === o.value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => aoSelecionar(o.value)}
            className={`group relative flex items-center gap-3 rounded-2xl border-2 p-3 text-left transition-all ${
              ativo
                ? 'border-primary bg-primary/5 ring-primary/30 shadow-octa-glow ring-4'
                : 'border-border/60 hover:border-primary/40 hover:bg-accent/40 bg-card'
            }`}
          >
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${
                ativo ? 'bg-octa-gradient text-white' : 'bg-muted text-muted-foreground'
              }`}
            >
              <Icone className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold leading-tight">{o.label}</p>
              {o.subtitle ? (
                <p className="text-muted-foreground truncate text-[11px]">{o.subtitle}</p>
              ) : null}
            </div>
            {ativo ? (
              <CheckCircle2
                className="text-primary octa-success-pop h-5 w-5 shrink-0"
                aria-hidden
              />
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function StepHeader({
  numero,
  titulo,
  completo,
}: {
  numero: number;
  titulo: string;
  completo: boolean;
}) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <div
        className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors ${
          completo
            ? 'bg-octa-gradient shadow-octa-glow text-white'
            : 'bg-muted text-muted-foreground'
        }`}
      >
        {completo ? <CheckCircle2 className="h-4 w-4" /> : numero}
      </div>
      <h2 className="text-base font-bold">{titulo}</h2>
    </div>
  );
}

/**
 * Contador regressivo de 5min do código.
 * Conta de 300s -> 0, mostra mm:ss.
 */
function ContadorRegressivo({ ativoAt }: { ativoAt: number }) {
  const [restante, setRestante] = useState(300);
  useEffect(() => {
    const t = setInterval(() => {
      const delta = Math.max(0, 300 - Math.floor((Date.now() - ativoAt) / 1000));
      setRestante(delta);
    }, 1000);
    return () => clearInterval(t);
  }, [ativoAt]);
  const min = Math.floor(restante / 60);
  const seg = restante % 60;
  return (
    <span className="tabular-nums">
      {String(min).padStart(2, '0')}:{String(seg).padStart(2, '0')}
    </span>
  );
}

export function GerarCodigoPage() {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const [seqVeiculo, setSeqVeiculo] = useState('');
  const [seqMaquina, setSeqMaquina] = useState('');
  const [seqTipoLavagem, setSeqTipoLavagem] = useState('');
  const [codigoGerado, setCodigoGerado] = useState<string | null>(null);
  const [erroDetalhado, setErroDetalhado] = useState<string | null>(null);
  const [ativoAt, setAtivoAt] = useState<number | null>(null);

  const veiculos = useQuery({
    queryKey: ['cliente-veiculos-ativos', user?.id],
    queryFn: async () => {
      const { data: cli } = await supabase
        .from('tab_clientes')
        .select('seq_cliente')
        .eq('cod_auth_user', user!.id)
        .single();
      const { data, error } = await supabase
        .from('tab_veiculos')
        .select('seq_veiculo, num_placa, cod_apelido')
        .eq('seq_cliente', cli?.seq_cliente ?? 0)
        .eq('cod_status', 'ATIVO');
      if (error) throw error;
      return (data ?? []) as Veiculo[];
    },
    enabled: !!user,
  });

  const maquinas = useQuery({
    queryKey: ['cliente-maquinas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_maquinas')
        .select('seq_maquina, nom_maquina, tab_unidades(nom_unidade)')
        .eq('ind_ativo', true);
      if (error) throw error;
      return (data ?? []) as unknown as Maquina[];
    },
  });

  const tiposLavagem = useQuery({
    queryKey: ['tipos-lavagem'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tab_tipos_lavagem')
        .select('seq_tipo_lavagem, cod_tipo_lavagem, nom_tipo_lavagem')
        .eq('ind_ativo', true)
        .order('seq_tipo_lavagem');
      if (error) throw error;
      return (data ?? []) as TipoLavagem[];
    },
  });

  const gerar = useMutation({
    mutationFn: async () => {
      setErroDetalhado(null);
      const { data, error } = await supabase.functions.invoke('gerar-codigo-validacao', {
        body: {
          seq_veiculo: Number(seqVeiculo),
          seq_maquina: Number(seqMaquina),
          seq_tipo_lavagem: Number(seqTipoLavagem),
          cod_origem_geracao: 'APP',
        },
      });
      if (error) {
        const msg = await extrairMensagemErro(error);
        throw new Error(msg);
      }
      if (data && typeof data === 'object' && 'erro' in data) {
        throw new Error(String((data as { erro: string }).erro));
      }
      return data as { cod_codigo: string };
    },
    onSuccess: (r) => {
      setCodigoGerado(r.cod_codigo);
      setAtivoAt(Date.now());
      toast.success('Código pronto!', {
        description: 'Válido por 5 minutos no totem da unidade.',
      });
    },
    onError: (e: Error) => {
      setErroDetalhado(e.message);
      toast.error('Falha ao gerar código', { description: traduzirErroSupabase(e) });
    },
  });

  // SUCESSO — WOW MOMENT: hero gigante, revelação cinematográfica
  if (codigoGerado) {
    const veicSel = veiculos.data?.find((v) => String(v.seq_veiculo) === seqVeiculo);
    const tipoSel = tiposLavagem.data?.find((t) => String(t.seq_tipo_lavagem) === seqTipoLavagem);
    const maqSel = maquinas.data?.find((m) => String(m.seq_maquina) === seqMaquina);
    // Cada dígito separado pra animar individualmente (stagger reveal estilo Apple Wallet)
    const digitos = codigoGerado.split('');

    return (
      <div className="bg-octa-hero relative min-h-screen overflow-hidden p-4 pb-24">
        {/* Blobs de fundo — mais opacos pra dar drama */}
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-gradient pointer-events-none absolute -left-32 -top-32 h-[28rem] w-[28rem]"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
        />
        <motion.div
          aria-hidden
          className="octa-blob bg-octa-accent pointer-events-none absolute -bottom-24 -right-24 h-80 w-80"
          style={{ animationDelay: '-5s' }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
        />

        <div className="relative z-10 mx-auto max-w-md space-y-5">
          {/* Botão voltar discreto */}
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="text-muted-foreground -ml-2 h-8 px-2"
            >
              <Link to="/">
                <ChevronLeft className="mr-1 h-4 w-4" />
                Voltar
              </Link>
            </Button>
          </motion.div>

          {/* Faixa "tudo pronto" — celebra antes do hero */}
          <motion.div
            className="flex items-center justify-center gap-2"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-700 ring-1 ring-emerald-500/30 dark:text-emerald-300">
              <motion.span
                className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                animate={{ scale: [1, 1.4, 1], opacity: [1, 0.5, 1] }}
                transition={{ duration: 1.6, repeat: Infinity }}
              />
              Código ativo
            </div>
          </motion.div>

          {/* HERO: o código em si — cinematográfico */}
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <Card
              variant="wallet"
              className="relative overflow-hidden rounded-[2rem] px-6 py-8 text-white"
            >
              {/* Luz superior tipo lens-flare */}
              <motion.div
                aria-hidden
                className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white/25 blur-3xl"
                animate={{ opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.div
                aria-hidden
                className="bg-white/18 pointer-events-none absolute -bottom-16 left-1/3 h-40 w-40 rounded-full blur-3xl"
                animate={{ opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
              />

              {/* Header tenant + timer */}
              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <motion.div
                    className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/25 ring-2 ring-white/40 backdrop-blur"
                    initial={{ rotate: -180, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                  >
                    <Sparkles className="h-5 w-5" />
                  </motion.div>
                  <span className="text-sm font-bold uppercase tracking-[0.16em]">
                    {tenant?.nom_tenant ?? 'Octa Pontos'}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 rounded-full bg-white/25 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider backdrop-blur">
                  <Clock className="h-3 w-3" />
                  {ativoAt ? <ContadorRegressivo ativoAt={ativoAt} /> : '05:00'}
                </div>
              </div>

              {/* Label */}
              <motion.p
                className="relative z-10 mt-8 text-center text-[11px] font-semibold uppercase tracking-[0.22em] text-white/80"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                Seu código
              </motion.p>

              {/* O HERO — dígito-a-dígito com stagger reveal */}
              <div className="relative z-10 mt-3 flex items-center justify-center gap-1.5 sm:gap-2">
                {digitos.map((d, i) => (
                  <motion.span
                    key={i}
                    className="inline-flex h-16 min-w-[2.5rem] items-center justify-center rounded-2xl bg-white/15 px-2 text-[2.75rem] font-extrabold tabular-nums leading-none tracking-tight text-white shadow-[inset_0_2px_8px_rgba(255,255,255,0.2),0_4px_16px_rgba(0,0,0,0.15)] ring-1 ring-white/30 backdrop-blur-sm sm:h-20 sm:text-[3.25rem]"
                    initial={{ opacity: 0, y: 30, rotateX: -90, scale: 0.6 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0, scale: 1 }}
                    transition={{
                      duration: 0.6,
                      delay: 0.6 + i * 0.08,
                      ease: [0.34, 1.56, 0.64, 1],
                    }}
                  >
                    {d}
                  </motion.span>
                ))}
              </div>

              {/* CTA microcopy + scanline animado */}
              <motion.div
                className="relative z-10 mt-6 flex flex-col items-center gap-1.5"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.4 }}
              >
                <div className="flex items-center gap-2 text-xs font-semibold text-white/90">
                  <motion.span
                    animate={{ y: [-2, 2, -2] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <ScanLine className="h-4 w-4" />
                  </motion.span>
                  Digite no totem da unidade
                </div>
              </motion.div>

              {/* Perforation */}
              <div className="relative -mx-6 mt-6 h-4">
                <div className="absolute inset-x-6 top-1/2 -translate-y-1/2 border-t-2 border-dashed border-white/40" />
                <div className="absolute -left-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-[hsl(var(--background))]" />
                <div className="absolute -right-2 top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-[hsl(var(--background))]" />
              </div>

              {/* Detalhes do ticket */}
              <motion.div
                className="relative z-10 grid grid-cols-3 gap-3 text-center"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.5, duration: 0.5 }}
              >
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/70">
                    Veículo
                  </p>
                  <p className="mt-1 text-sm font-bold tracking-wider">
                    {veicSel?.num_placa ?? '—'}
                  </p>
                  {veicSel?.cod_apelido ? (
                    <p className="text-[11px] text-white/70">{veicSel.cod_apelido}</p>
                  ) : null}
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/70">
                    Lavagem
                  </p>
                  <p className="mt-1 text-sm font-bold">{tipoSel?.nom_tipo_lavagem ?? '—'}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-white/70">
                    Unidade
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm font-bold leading-tight">
                    {maqSel?.tab_unidades?.nom_unidade ?? '—'}
                  </p>
                </div>
              </motion.div>
            </Card>
          </motion.div>

          {/* Instruções */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7, duration: 0.5 }}
          >
            <Card variant="glass" className="rounded-2xl border-white/40">
              <CardContent className="space-y-3 p-5">
                <p className="text-muted-foreground text-[10px] font-semibold uppercase tracking-[0.18em]">
                  Como usar
                </p>
                <ol className="space-y-2 text-sm">
                  {[
                    'Vá até o totem da unidade selecionada.',
                    'Digite o código de 6 dígitos no painel.',
                    'A máquina libera e a lavagem começa automaticamente.',
                  ].map((txt, i) => (
                    <motion.li
                      key={i}
                      className="flex gap-3"
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 1.8 + i * 0.08 }}
                    >
                      <span className="bg-octa-gradient flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
                        {i + 1}
                      </span>
                      <span>{txt}</span>
                    </motion.li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.0, duration: 0.5 }}
          >
            <Button
              variant="outline"
              onClick={() => {
                setCodigoGerado(null);
                setAtivoAt(null);
              }}
              className="h-12 w-full rounded-xl border-2 text-base font-semibold"
            >
              Gerar outro código
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  // FORMULÁRIO de seleção
  const veicSel = !!seqVeiculo;
  const tipoSel = !!seqTipoLavagem;
  const maqSel = !!seqMaquina;
  const pronto = veicSel && tipoSel && maqSel;

  return (
    <div className="relative min-h-screen overflow-hidden p-4 pb-24">
      {/* Blob decorativo discreto */}
      <div
        aria-hidden
        className="octa-blob bg-octa-gradient pointer-events-none absolute -left-32 -top-32 h-72 w-72"
        style={{ opacity: 0.35 }}
      />

      <div className="relative z-10 mx-auto max-w-2xl space-y-6">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="text-muted-foreground -ml-2 mb-3 h-8 px-2"
          >
            <Link to="/">
              <ChevronLeft className="mr-1 h-4 w-4" />
              Voltar
            </Link>
          </Button>
          <h1 className="text-display-md font-extrabold tracking-tight">Lavar agora</h1>
          <p className="text-muted-foreground mt-1.5 text-sm">
            Escolha o veículo, o tipo de lavagem e a unidade. Em 3 toques o código fica pronto.
          </p>
        </motion.div>

        {erroDetalhado && (
          <Card className="octa-scale-in border-rose-200 bg-rose-50 dark:border-rose-900 dark:bg-rose-950/40">
            <CardContent className="flex items-start gap-3 p-4 text-sm">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600 dark:text-rose-300" />
              <div>
                <p className="font-semibold text-rose-900 dark:text-rose-100">
                  Não foi possível gerar
                </p>
                <p className="mt-0.5 text-xs text-rose-700 dark:text-rose-200">{erroDetalhado}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1 — Veículo */}
        <section className="octa-fade-in-up" style={{ animationDelay: '0.05s' }}>
          <StepHeader numero={1} titulo="Qual veículo?" completo={veicSel} />
          {veiculos.isLoading ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <Skeleton className="h-16 rounded-2xl" />
              <Skeleton className="h-16 rounded-2xl" />
            </div>
          ) : veiculos.data && veiculos.data.length > 0 ? (
            <PillSelect
              opcoes={veiculos.data.map((v) => ({
                value: String(v.seq_veiculo),
                label: v.num_placa,
                ...(v.cod_apelido ? { subtitle: v.cod_apelido } : {}),
              }))}
              selecionado={seqVeiculo}
              aoSelecionar={setSeqVeiculo}
              icone={CarFront}
            />
          ) : (
            <Card variant="elevated" className="rounded-2xl">
              <CardContent className="space-y-3 p-5 text-center">
                <CarFront className="text-muted-foreground mx-auto h-8 w-8" />
                <p className="text-sm font-medium">Nenhum veículo cadastrado ainda.</p>
                <Button asChild className="bg-octa-gradient h-10 rounded-xl text-white">
                  <Link to="/veiculos/adicionar">Adicionar veículo</Link>
                </Button>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Step 2 — Tipo de lavagem */}
        <section className="octa-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <StepHeader numero={2} titulo="Qual lavagem?" completo={tipoSel} />
          {tiposLavagem.isLoading ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <Skeleton className="h-16 rounded-2xl" />
              <Skeleton className="h-16 rounded-2xl" />
            </div>
          ) : tiposLavagem.data && tiposLavagem.data.length > 0 ? (
            <PillSelect
              opcoes={tiposLavagem.data.map((t) => ({
                value: String(t.seq_tipo_lavagem),
                label: t.nom_tipo_lavagem,
                subtitle: t.cod_tipo_lavagem,
              }))}
              selecionado={seqTipoLavagem}
              aoSelecionar={setSeqTipoLavagem}
              icone={Droplets}
            />
          ) : (
            <p className="text-muted-foreground text-xs">
              Nenhum tipo de lavagem disponível no momento.
            </p>
          )}
        </section>

        {/* Step 3 — Unidade/máquina */}
        <section className="octa-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <StepHeader numero={3} titulo="Em qual unidade?" completo={maqSel} />
          {maquinas.isLoading ? (
            <div className="grid gap-2 sm:grid-cols-2">
              <Skeleton className="h-16 rounded-2xl" />
              <Skeleton className="h-16 rounded-2xl" />
            </div>
          ) : maquinas.data && maquinas.data.length > 0 ? (
            <PillSelect
              opcoes={maquinas.data.map((m) => ({
                value: String(m.seq_maquina),
                label: m.tab_unidades?.nom_unidade ?? 'Unidade',
                subtitle: m.nom_maquina,
              }))}
              selecionado={seqMaquina}
              aoSelecionar={setSeqMaquina}
              icone={MapPin}
            />
          ) : (
            <p className="text-muted-foreground text-xs">Nenhuma máquina disponível.</p>
          )}
        </section>

        {/* CTA fixa no fundo (sticky em mobile) */}
        <div
          className="bg-background/80 sticky bottom-0 -mx-4 mt-4 border-t px-4 py-3 backdrop-blur sm:relative sm:mx-0 sm:border-0 sm:bg-transparent sm:p-0"
          style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 12px)' }}
        >
          <motion.div
            animate={pronto ? { scale: [1, 1.02, 1] } : { scale: 1 }}
            transition={{ duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
          >
            <Button
              onClick={() => gerar.mutate()}
              disabled={gerar.isPending || !pronto}
              className={`h-14 w-full rounded-2xl text-base font-bold transition-transform active:scale-[0.98] ${
                pronto
                  ? 'octa-cta-magic octa-pulse-halo text-white hover:scale-[1.01]'
                  : 'bg-muted text-muted-foreground'
              }`}
            >
              <AnimatePresence mode="wait" initial={false}>
                {gerar.isPending ? (
                  <motion.span
                    key="loading"
                    className="flex items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <ScanLine className="mr-2 h-5 w-5 animate-pulse" />
                    Gerando código…
                  </motion.span>
                ) : pronto ? (
                  <motion.span
                    key="ready"
                    className="flex items-center"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                  >
                    <ScanLine className="mr-2 h-5 w-5" />
                    Gerar código
                  </motion.span>
                ) : (
                  <motion.span
                    key="incompleto"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                  >
                    Complete os 3 passos acima
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
